import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createUser, clearFlags } from '../../../store/slices/userSlice';
import {
  Modal, Form, Switch,
  CustomButton, CustomInput, CustomSelect, Colors, Constants, showToast
} from '../../../utils/commonImports';

const CreateUserModal = ({ isOpen, onClose }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { loading, isCreated, error } = useSelector((state) => state.user);

  useEffect(() => {
    if (isCreated) {
      showToast('User created successfully', 'success');
      form.resetFields();
      onClose();
      dispatch(clearFlags());
    }

    if (error) {
      showToast(error, 'error');
    }
  }, [isCreated, error]);

  useEffect(() => {
    if (!isOpen) {
      form.resetFields();
    }
  }, [isOpen, form]);

  const handleSubmit = (values) => {
    dispatch(createUser(values));
  };

  return (
    <Modal
      title="Create User"
      open={isOpen}
      onCancel={onClose}
      maskClosable={false}
      centered
      width={600}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{ marginTop: '20px' }}
      >
        <div style={{ display: 'flex', gap: '16px' }}>
          <Form.Item
            name="firstName"
            label="First Name"
            rules={[{ required: true, message: 'Please enter first name' }]}
            style={{ flex: 1 }}
          >
            <CustomInput placeholder="Enter first name" />
          </Form.Item>

          <Form.Item
            name="lastName"
            label="Last Name"
            style={{ flex: 1 }}
          >
            <CustomInput placeholder="Enter last name" />
          </Form.Item>
        </div>

        <div style={{ display: 'flex', gap: '16px' }}>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter valid email' }
            ]}
            style={{ flex: 1 }}
          >
            <CustomInput placeholder="Enter email" />
          </Form.Item>

          <Form.Item
            name="contactNumber"
            label="Contact Number"
            rules={[{ required: true, message: 'Please enter contact number' }]}
            style={{ flex: 1 }}
          >
            <CustomInput placeholder="Enter contact number" />
          </Form.Item>
        </div>

        <div style={{ display: 'flex', gap: '16px' }}>
          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please enter password' },
              { min: 6, message: 'Password must be at least 6 characters' }
            ]}
            style={{ flex: 1 }}
          >
            <CustomInput type="password" placeholder="Enter password" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            rules={[
              { required: true, message: 'Please confirm password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
            style={{ flex: 1 }}
          >
            <CustomInput type="password" placeholder="Confirm password" />
          </Form.Item>
        </div>

        <Form.Item
          name="role"
          label="Role"
          rules={[{ required: true, message: 'Please select role' }]}
          initialValue="user"
          style={{ width: '50%' }}
        >
          <CustomSelect 
            placeholder="Select role"
            options={[
              { value: 'user', label: 'User' },
              { value: 'admin', label: 'Admin' }
            ]}
          />
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => prevValues.role !== currentValues.role}
        >
          {({ getFieldValue }) => {
            const role = getFieldValue('role');
            if (role === Constants.ROLES.ADMIN) {
              return (
                <Form.Item
                  name="can_approve"
                  label="Can Approve Transactions"
                  valuePropName="checked"
                  initialValue={false}
                >
                  <Switch 
                    checkedChildren="Yes" 
                    unCheckedChildren="No"
                  />
                </Form.Item>
              );
            }
            return null;
          }}
        </Form.Item>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
          <CustomButton
            variant="outline"
            color={Colors.error}
            onClick={onClose}
            disabled={loading}
            width={140}
            height={32}
            fontSize={12}
          >
            Cancel
          </CustomButton>
          <CustomButton
            htmlType="submit"
            disabled={loading}
            width={140}
            height={32}
            fontSize={12}
          >
            Create User
          </CustomButton>
        </div>
      </Form>
    </Modal>
  );
};

export default CreateUserModal;